import { Circle, RadioButtonUnchecked, Clear, Search } from '@mui/icons-material'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  Typography,
  Rating,
  IconButton,
  TablePagination,
  Tooltip,
  Grow,
  InputAdornment,
  TextField,
  TableFooter,
  Stack,
  useTheme,
  Button,
} from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { UsersTechnology } from '../../store/types'
import { useTranslation } from 'react-i18next'
import { formatTechnologyType } from '../../store/utils'
import { visuallyHidden } from '@mui/utils'
import { StyledContainerRowBetween } from '../../styles/ContainerStyles'
import SelectTechnology from '../Form/SelectTechnology'
import TechnologyLogo from '../Logo/TechnologyLogo'
import { defaultPageSize, defaultRowsPerPageOptions, smallDefaultPageSize } from '../../config/defaultPagination'
import { Order } from '../../store/types/components/table/order'
import { StyledTableSortLabel } from '../../styles/TableStyles'
import { StyledFooterCell, StyledHeaderCell } from '../../styles/WorklogTableStyles'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import TechTypeFilterButton from './TechTypeFilterButton'

const nameComparator = (a: UsersTechnology, b: UsersTechnology) => {
  if (b.name < a.name) {
    return -1
  }
  if (b.name > a.name) {
    return 1
  }
  return 0
}

const levelComparator = (a: UsersTechnology, b: UsersTechnology, orderBy: keyof UsersTechnology) => {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator<Key extends keyof UsersTechnology>(
  order: Order,
  orderBy: Key
): (a: UsersTechnology, b: UsersTechnology) => number {
  if (orderBy === 'level') {
    return order === 'desc' ? (a, b) => levelComparator(a, b, orderBy) : (a, b) => -levelComparator(a, b, orderBy)
  }

  return order === 'desc' ? (a, b) => nameComparator(a, b) : (a, b) => -nameComparator(a, b)
}

function stableSort<UsersTechnology>(
  array: UsersTechnology[],
  comparator: (a: UsersTechnology, b: UsersTechnology) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [UsersTechnology, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

type TechStackTableProps = {
  editable: boolean | undefined
  replace: (technologies: UsersTechnology[]) => void
  watchedTechnologies: UsersTechnology[] | undefined
  userTechnologies: UsersTechnology[] | undefined
  smallDefaultTable?: boolean
}

const TechStackTable = ({
  editable,
  replace,
  watchedTechnologies,
  userTechnologies,
  smallDefaultTable,
}: TechStackTableProps) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const [selected, setSelected] = useState<UsersTechnology[]>([])
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [technologies, setTechnologies] = useState<UsersTechnology[]>([]) // Assigned to the user

  useMemo(() => {
    if (watchedTechnologies !== undefined) {
      setTechnologies(watchedTechnologies)
      return
    }

    if (userTechnologies !== undefined) {
      setTechnologies(userTechnologies)
      return
    }

    setTechnologies([])
  }, [userTechnologies, watchedTechnologies])

  const availableTypes = useMemo(() => {
    return Array.from(new Set(technologies.map(tech => tech.type))).sort()
  }, [technologies])

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev => (prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]))
    setPage(0)
  }

  const handleClick = (event: React.MouseEvent<unknown>, technology: UsersTechnology) => {
    const indexes = selected.map(entry => entry.technologyId)
    const selectedIndex = indexes.indexOf(technology.technologyId)
    let newSelected: UsersTechnology[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, technology)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }

    setSelected(newSelected)
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(technologies)
      return
    }
    setSelected([])
  }

  const isSelected = (technology: UsersTechnology) => {
    let indexes = selected.map(entry => entry.technologyId)
    return indexes.indexOf(technology.technologyId) !== -1
  }

  const handleDeleteSelected = () => {
    let selectedIndexes = selected.map(entry => entry.technologyId)
    replace(technologies.filter(technology => !selectedIndexes.includes(technology.technologyId)))
    setSelected([])
  }

  const deleteVisible = useMemo(() => {
    return selected.length !== 0
  }, [selected])

  const [order, setOrder] = React.useState<Order>('asc')
  const [orderBy, setOrderBy] = React.useState<keyof UsersTechnology>('name')

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof UsersTechnology) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setPage(0)
  }

  const createSortHandler = (property: keyof UsersTechnology) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property)
  }

  const [input, setInput] = useState('')

  let rowCount = technologies.length
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem(LocalStorageTypes.TechStackRows)
      ? parseInt(localStorage.getItem(LocalStorageTypes.TechStackRows)!)
      : !smallDefaultTable
      ? defaultPageSize
      : smallDefaultPageSize
  )

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    localStorage.setItem(LocalStorageTypes.TechStackRows, String(parseInt(event.target.value, 10)))
    setPage(0)
  }

  const filteredTechnologies = useMemo(() => {
    return technologies.filter(technology => {
      const matchesSearch =
        input === '' ||
        technology.name.toLowerCase().includes(input.toLowerCase()) ||
        formatTechnologyType(technology.type).toLowerCase().includes(input.toLowerCase())
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(technology.type)
      return matchesSearch && matchesType
    })
  }, [input, technologies, selectedTypes])

  const visibleRows = useMemo(
    () =>
      stableSort(
        technologies.filter(
          technology =>
            (technology.name.toLowerCase().includes(input.toLowerCase()) ||
              formatTechnologyType(technology.type).toLowerCase().includes(input.toLowerCase())) &&
            (selectedTypes.length === 0 || selectedTypes.includes(technology.type))
        ),
        getComparator(order, orderBy)
      ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, technologies, input, selectedTypes]
  )

  useEffect(() => {
    setInput('')
  }, [editable])

  const pageSize = Math.min(rowsPerPage, defaultPageSize)
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * pageSize - technologies.length) : pageSize - visibleRows.length

  useEffect(() => {
    if (emptyRows === rowsPerPage && page !== 0) {
      setPage(page - 1)
    }
  }, [emptyRows, page, rowsPerPage, technologies])

  const typeFilterButton = (
    <TechTypeFilterButton
      availableTypes={availableTypes}
      selectedTypes={selectedTypes}
      onToggle={handleTypeToggle}
      onClear={() => {
        setSelectedTypes([])
        setPage(0)
      }}
    />
  )

  return (
    <Stack direction="column" sx={{ gap: 2 }}>
      {editable ? (
        <StyledContainerRowBetween>
          <SelectTechnology usersTechnologies={technologies} replace={replace} />

          <Stack direction="row" alignItems="center" gap={1}>
            {typeFilterButton}
            <Grow in={deleteVisible}>
              <Button
                size="medium"
                variant="contained"
                color="secondary"
                onClick={() => handleDeleteSelected()}
                sx={{
                  display: deleteVisible ? '' : 'none',
                }}
              >
                <Clear />
                {t('page.usersProfile.delete')}
              </Button>
            </Grow>
          </Stack>
        </StyledContainerRowBetween>
      ) : (
        <Stack direction="row" alignItems="center" gap={1}>
          <TextField
            placeholder={t('components.placeholder.search')}
            size="small"
            fullWidth={true}
            value={input}
            onChange={e => {
              setInput(e.target.value)
              setPage(0)
            }}
            sx={{
              svg: {
                color: 'text.base',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              inputProps: {
                'aria-labelledby': 'tech-header',
              },
            }}
          />
          {typeFilterButton}
        </Stack>
      )}

      <TableContainer sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'background.elevation8' }}>
        <Table sx={{ minWidth: 150 }} size="small">
          <TableHead>
            <TableRow>
              {editable && (
                <StyledHeaderCell
                  id={'header-checkbox-all'}
                  aria-label={t('aria.myProfile.checkboxSelectAll')}
                  padding="checkbox"
                >
                  <Checkbox
                    checked={rowCount > 0 && selected.length === rowCount}
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-labelledby': 'header-checkbox-all' }}
                  />
                </StyledHeaderCell>
              )}

              <StyledHeaderCell
                sx={{ width: '30%', px: editable ? 1 : 3, py: 1.5 }}
                sortDirection={orderBy === 'name' ? order : false}
              >
                <StyledTableSortLabel
                  sx={{
                    color: 'text.base',
                    '&.Mui-active': {
                      color: 'text.highEmphasis',
                    },
                  }}
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={createSortHandler('name')}
                >
                  {t('components.table.header.technology')}
                  {orderBy === 'name' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </StyledHeaderCell>

              <StyledHeaderCell
                sx={{ width: '100%', px: 2.2, py: 1.5 }}
                sortDirection={orderBy === 'level' ? order : false}
              >
                <StyledTableSortLabel
                  active={orderBy === 'level'}
                  direction={orderBy === 'level' ? order : 'asc'}
                  onClick={createSortHandler('level')}
                  sx={{
                    color: 'text.base',
                    '&.Mui-active': {
                      color: 'text.highEmphasis',
                    },
                  }}
                >
                  {t('components.table.header.level')}
                  {orderBy === 'level' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </StyledHeaderCell>

              {editable && (
                <StyledHeaderCell sx={{ width: '10%' }} align="center">
                  {t('components.table.header.action')}
                </StyledHeaderCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {technologies.length === 0 || visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ height: 50 * Math.min(rowsPerPage, defaultPageSize) }} align="center">
                  <Typography variant="body1" fontWeight={500}>
                    {t('components.table.cell.noTechnologies.title')}
                  </Typography>
                  {editable && (
                    <Typography variant="body2">{t('components.table.cell.noTechnologies.subtitle')}</Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map(technology => {
                const isItemSelected = isSelected(technology)
                return (
                  <TableRow
                    sx={{ height: 50, borderBottom: '1px solid', borderColor: 'background.elevation9' }}
                    key={`table-row-${technology.technologyId}`}
                  >
                    {editable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={event => handleClick(event, technology)}
                          inputProps={{ 'aria-label': `checkbox-select-technology-${technology.name}` }}
                        />
                      </TableCell>
                    )}

                    <TableCell sx={{ display: 'flex', alignItems: 'center', px: editable ? 1 : 3, gap: 2, border: 0 }}>
                      <TechnologyLogo size={29} technology={technology} />
                      <Box>
                        <Typography component={'h4'} variant="subtitle2">
                          {technology.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                        >
                          {formatTechnologyType(technology.type)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ border: 0 }}>
                      <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                        <legend
                          style={{
                            position: 'absolute',
                            width: 1,
                            height: 1,
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {technology.name} {t('components.table.header.level')}
                        </legend>
                        <Rating
                          name={`customized-color-${technology.name}`}
                          defaultValue={1}
                          value={technology.level / 2}
                          disabled={!editable}
                          onChange={(event, newValue) => {
                            replace(
                              technologies.map(entry => {
                                if (entry.technologyId === technology.technologyId) {
                                  entry.level = 1

                                  if (newValue !== null) {
                                    entry.level = newValue * 2
                                  }
                                }

                                return entry
                              })
                            )
                          }}
                          getLabelText={(value: number) => `${value} Circle${value !== 1 ? 's' : ''}`}
                          precision={0.5}
                          icon={
                            <Circle
                              sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }}
                              aria-hidden={'false'}
                            />
                          }
                          emptyIcon={
                            <RadioButtonUnchecked
                              sx={{ width: 14, mr: 0.25, color: theme.palette.primary.main }}
                              aria-hidden={'false'}
                            />
                          }
                          aria-label={`${technology.name} ${t('components.table.header.level')}`}
                        />
                      </fieldset>
                    </TableCell>

                    {editable && (
                      <TableCell align="center">
                        <Tooltip title={t('components.tooltip.delete')} placement="right-end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              replace(technologies.filter(entry => entry.technologyId !== technology.technologyId))
                            }}
                          >
                            <Clear sx={{ height: 16, width: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
            {emptyRows > 0 && visibleRows.length > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={4} />
              </TableRow>
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <StyledFooterCell colSpan={10}>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={defaultRowsPerPageOptions}
                  count={filteredTechnologies.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  labelRowsPerPage={t('components.table.pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) => {
                    return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    position: 'sticky',
                    right: 16,
                    zIndex: 10,
                    width: 'fit-content',
                    float: 'right',
                  }}
                  SelectProps={{
                    inputProps: {
                      'aria-label': t('components.table.pagination.rowsPerPage'),
                      'aria-labelledby': 'rowsPage',
                    },
                    id: 'rowsPage',
                  }}
                />
              </StyledFooterCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default TechStackTable
